import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import API from './api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const [menuPermissions, setMenuPermissions] = useState({});

  // Verify token on mount and persist token to localStorage
  useEffect(() => {
    if (token) {
      localStorage.setItem('token', token);
      Promise.all([
        API.get('/auth/me'),
        API.get('/auth/menus'),
      ])
        .then(([meRes, menusRes]) => {
          setUser(meRes.data);
          const permMap = {};
          menusRes.data.forEach(p => {
            permMap[p.menu_key] = {
              can_view: p.can_view,
              can_create: p.can_create,
              can_edit: p.can_edit,
              can_delete: p.can_delete,
            };
          });
          setMenuPermissions(permMap);
        })
        .catch(() => {
          setToken(null);
          setUser(null);
          setMenuPermissions({});
          localStorage.removeItem('token');
        })
        .finally(() => setLoading(false));
    } else {
      localStorage.removeItem('token');
      setLoading(false);
    }
  }, [token]);

  const login = useCallback(async (username, password) => {
    const res = await API.post('/auth/login', { username, password });
    const { token: newToken, user: userData } = res.data;
    setToken(newToken);
    setUser(userData);
    return userData;
  }, []);

  const loadMenus = useCallback(async () => {
    try {
      const res = await API.get('/auth/menus');
      const permMap = {};
      res.data.forEach(p => {
        permMap[p.menu_key] = {
          can_view: p.can_view,
          can_create: p.can_create,
          can_edit: p.can_edit,
          can_delete: p.can_delete,
        };
      });
      setMenuPermissions(permMap);
    } catch (err) {
      console.error('Failed to load menus', err);
    }
  }, []);

  const logout = useCallback(() => {
    setToken(null);
    setUser(null);
    setMenuPermissions({});
    localStorage.removeItem('token');
  }, []);

  const hasMenuAccess = useCallback((menuKey) => {
    if (!menuKey) return true;
    // Admin always has access
    if (user?.role === 'admin') return true;
    return menuPermissions[menuKey]?.can_view === true;
  }, [user, menuPermissions]);

  const hasMenuAction = useCallback((menuKey, action) => {
    if (!menuKey || !action) return true;
    // Admin always has full access
    if (user?.role === 'admin') return true;
    const perm = menuPermissions[menuKey];
    if (!perm) return false;
    if (!perm.can_view) return false;
    if (action === 'create') return perm.can_create === true;
    if (action === 'edit') return perm.can_edit === true;
    if (action === 'delete') return perm.can_delete === true;
    return false;
  }, [user, menuPermissions]);

  return (
    <AuthContext.Provider value={{ user, token, loading, login, logout, isAuthenticated: !!token, menuPermissions, loadMenus, hasMenuAccess, hasMenuAction }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}

export default AuthContext;
